// Export every live + archived note as a ZIP bundle:
//   - notes.html             → polished, printable HTML view (opens anywhere)
//   - notes.md               → one combined markdown file (fallback for devs)
//   - images/<slug>-N.jpg    → referenced images
//   - voice/<slug>-N.webm    → voice notes
//   - files/<slug>-N.pdf     → documents
// When supported we use the File System Access API's `showSaveFilePicker`
// so the user can choose where to save; otherwise we fall back to a
// classic `<a download>` trigger.
import JSZip from "jszip";
import { filterNotes } from "@/lib/offlineStore";
import { fileUrl } from "@/lib/api";
import { format } from "date-fns";

function safeFmt(iso) {
  try { return format(new Date(iso), "yyyy-MM-dd HH:mm"); } catch { return iso; }
}

function slugify(s) {
  return (s || "note")
    .toString().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "note";
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Lightweight content normaliser — the note.content field holds either
// plain text or sanitized rich-text HTML. We trust what we already stored
// (sanitisation happened before the write) and just unwrap any outer
// <div>/<p>-only wrappers so the exported HTML renders cleanly.
function noteContentHtml(n) {
  const raw = n.content || "";
  if (!raw) return "";
  // If it already looks like HTML, pass through; otherwise convert line
  // breaks to <br> so plain text stays readable.
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return escapeHtml(raw).replace(/\n/g, "<br>");
}

function noteToMarkdown(n, mediaRefs) {
  const lines = [];
  lines.push(`# ${n.title || "Untitled"}`);
  lines.push("");
  const meta = [];
  if (n.created_at) meta.push(`Created: ${safeFmt(n.created_at)}`);
  if (n.updated_at) meta.push(`Updated: ${safeFmt(n.updated_at)}`);
  if (n.reminder) meta.push(`Reminder: ${safeFmt(n.reminder)}`);
  if (n.labels?.length) meta.push(`Labels: ${n.labels.join(", ")}`);
  if (n.pinned) meta.push("Pinned");
  if (n.color && n.color !== "default") meta.push(`Color: ${n.color}`);
  if (meta.length) {
    lines.push(`> ${meta.join(" | ")}`);
    lines.push("");
  }
  if (n.is_checklist) {
    for (const it of (n.checklist_items || [])) {
      lines.push(`- [${it.checked ? "x" : " "}] ${it.text || ""}`);
    }
  } else if (n.content) {
    lines.push(n.content);
  }
  if (mediaRefs.images.length) {
    lines.push("");
    lines.push("### Images");
    for (const p of mediaRefs.images) lines.push(`![](${p.path})`);
  }
  if (mediaRefs.voice.length) {
    lines.push("");
    lines.push("### Voice notes");
    for (const v of mediaRefs.voice) {
      lines.push(`- [${v.name}](${v.path})${v.transcript ? ` — _"${v.transcript}"_` : ""}`);
    }
  }
  if (mediaRefs.docs.length) {
    lines.push("");
    lines.push("### Files");
    for (const d of mediaRefs.docs) lines.push(`- [${d.name}](${d.path})`);
  }
  lines.push("", "---", "");
  return lines.join("\n");
}

function noteToHtmlCard(n, mediaRefs, index) {
  const metaBits = [];
  if (n.created_at) metaBits.push(`<span>Created ${safeFmt(n.created_at)}</span>`);
  if (n.updated_at && n.updated_at !== n.created_at) metaBits.push(`<span>Updated ${safeFmt(n.updated_at)}</span>`);
  if (n.reminder) metaBits.push(`<span class="pill">Reminder ${safeFmt(n.reminder)}</span>`);
  if (n.pinned) metaBits.push(`<span class="pill pin">Pinned</span>`);
  if (n.labels?.length) {
    for (const l of n.labels) metaBits.push(`<span class="pill tag">${escapeHtml(l)}</span>`);
  }

  const parts = [];
  parts.push(`<article class="note color-${escapeHtml(n.color || "default")}" id="n-${index}">`);
  parts.push(`<header><h2>${escapeHtml(n.title || "Untitled")}</h2>${
    metaBits.length ? `<div class="meta">${metaBits.join("")}</div>` : ""
  }</header>`);

  if (mediaRefs.images.length) {
    parts.push(`<div class="gallery">`);
    for (const p of mediaRefs.images) {
      // Prefer the embedded base64 thumb (so the HTML works standalone);
      // fall back to the relative path inside the zip if the thumbnailer
      // couldn't process the file (e.g. SVG with embedded fonts).
      const src = p.thumb || p.path;
      parts.push(`<a href="${escapeHtml(p.path)}" target="_blank" rel="noopener"><img src="${escapeHtml(src)}" alt="" loading="lazy"></a>`);
    }
    parts.push(`</div>`);
  }

  if (n.is_checklist) {
    parts.push(`<ul class="checklist">`);
    for (const it of (n.checklist_items || [])) {
      parts.push(`<li class="${it.checked ? "done" : ""}"><span class="box">${it.checked ? "&check;" : ""}</span>${escapeHtml(it.text || "")}</li>`);
    }
    parts.push(`</ul>`);
  } else if (n.content) {
    parts.push(`<div class="body">${noteContentHtml(n)}</div>`);
  }

  if (mediaRefs.voice.length) {
    parts.push(`<section class="attachments"><h3>Voice notes</h3>`);
    for (const v of mediaRefs.voice) {
      parts.push(`<div class="voice"><audio controls src="${escapeHtml(v.path)}"></audio><span class="fname">${escapeHtml(v.name)}</span>${v.transcript ? `<p class="transcript">&ldquo;${escapeHtml(v.transcript)}&rdquo;</p>` : ""}</div>`);
    }
    parts.push(`</section>`);
  }

  if (mediaRefs.docs.length) {
    parts.push(`<section class="attachments"><h3>Files</h3><ul class="files">`);
    for (const d of mediaRefs.docs) {
      parts.push(`<li><a href="${escapeHtml(d.path)}" target="_blank" rel="noopener">${escapeHtml(d.name)}</a></li>`);
    }
    parts.push(`</ul></section>`);
  }

  parts.push(`</article>`);
  return parts.join("");
}

function buildHtmlDocument(cards, stats) {
  // Keep CSS self-contained so a double-click on notes.html on any device
  // (phone, tablet, Chromebook, Windows) renders the same polished view.
  const css = `
    *,*:before,*:after{box-sizing:border-box}
    html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;color:#2b2621;background:#faf6f0;line-height:1.55}
    .wrap{max-width:960px;margin:0 auto;padding:48px 20px 72px}
    header.doc{text-align:center;margin-bottom:40px}
    header.doc h1{font-family:"Georgia","Times New Roman",serif;font-size:38px;margin:0 0 8px;color:#8a4a1e}
    header.doc p{margin:4px 0;color:#71655a;font-size:14px}
    .note{background:#fff;border:1px solid #e9e0d2;border-radius:18px;padding:22px 26px;margin:0 0 22px;box-shadow:0 1px 2px rgba(0,0,0,.03)}
    .note header{margin-bottom:12px;border-bottom:1px dashed #ece2d2;padding-bottom:10px}
    .note h2{font-family:"Georgia",serif;font-size:22px;margin:0 0 6px;color:#2b2621}
    .note h3{font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:#8a7968;margin:18px 0 8px}
    .meta{display:flex;flex-wrap:wrap;gap:6px 14px;font-size:12px;color:#8a7968}
    .pill{background:#f2ead9;border-radius:999px;padding:2px 10px;color:#6b5a46;font-size:11px}
    .pill.pin{background:#ffe9b3;color:#8a4a1e}
    .pill.tag{background:#e4dbc8;color:#5a4a30}
    .body{white-space:pre-wrap}
    .body p{margin:0 0 10px}
    .body ul,.body ol{margin:0 0 10px 22px}
    .body img{max-width:100%;height:auto;border-radius:10px}
    .checklist{list-style:none;padding:0;margin:0}
    .checklist li{display:flex;align-items:flex-start;gap:10px;padding:3px 0;font-size:15px}
    .checklist li.done{color:#8a7968;text-decoration:line-through}
    .checklist .box{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border:1.5px solid #a89986;border-radius:4px;margin-top:3px;font-size:11px;color:#2b2621;background:#fff;shrink:0}
    .checklist li.done .box{background:#2b2621;color:#fff;border-color:#2b2621}
    .gallery{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px;margin:14px 0}
    .gallery a{display:block;overflow:hidden;border-radius:10px;line-height:0;background:#f3ead9}
    .gallery img{width:100%;height:auto;max-height:420px;object-fit:cover;display:block;transition:transform .2s}
    .gallery img:hover{transform:scale(1.02)}
    .attachments{margin-top:14px}
    .voice{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:6px 0}
    .voice audio{height:36px}
    .voice .fname{font-size:12px;color:#8a7968}
    .transcript{flex-basis:100%;margin:4px 0 0;font-size:13px;color:#5a4a30;font-style:italic;background:#fdfaf2;padding:8px 12px;border-radius:8px;border-left:3px solid #c99e5a}
    .files{list-style:none;padding:0;margin:0}
    .files li{padding:3px 0}
    .files a{color:#8a4a1e;text-decoration:none;border-bottom:1px dotted #c99e5a}
    .color-amber{background:#fff8e8}
    .color-rose{background:#fde8ea}
    .color-sky{background:#e8f3ff}
    .color-mint{background:#e4f5ec}
    .color-lilac{background:#f0e8fa}
    .color-sand{background:#f6ede0}
    .color-slate{background:#e8ebf0}
    @media print{
      body{background:#fff}
      .wrap{padding:0}
      .note{break-inside:avoid;page-break-inside:avoid;box-shadow:none;border:1px solid #ddd}
    }
    @media (max-width:620px){
      .wrap{padding:28px 14px}
      header.doc h1{font-size:28px}
      .note{padding:16px 18px;border-radius:14px}
    }
  `;
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(`Mango Notes — ${stats.stamp}`)}</title>
<style>${css}</style>
</head>
<body>
<div class="wrap">
  <header class="doc">
    <h1>Mango Notes</h1>
    <p>${escapeHtml(`Exported ${stats.total} note${stats.total === 1 ? "" : "s"} on ${stats.stamp}`)}</p>
    <p>${escapeHtml(`${stats.live} active · ${stats.archived} archived`)}</p>
  </header>
  <main>
    ${cards.join("\n")}
  </main>
</div>
</body>
</html>`;
}

async function fetchBinary(path) {
  // `fileUrl` returns the public download URL under /api/files/<path>.
  const res = await fetch(fileUrl(path));
  if (!res.ok) throw new Error(`fetch ${path} failed`);
  return res.blob();
}

// Convert a blob to a base64 data URL, preserving the original bytes
// (and therefore the full image quality). We used to downscale through
// a <canvas> at 800 px / q=0.78 but the result looked blurry/pixelated,
// especially when the user prints the HTML to PDF or opens the thumbnail
// on a hi-dpi display. For images larger than 2.5 MB we fall back to a
// 2000 px canvas resize so the HTML stays reasonably sized.
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

async function resizeBlobForEmbed(blob) {
  try {
    const url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const MAX = 2000;
    const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    // High-quality down-scaling (default is "low" on some browsers).
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch (err) {
    console.warn("[export] resize failed, embedding original:", err?.message || err);
    return null;
  }
}

async function blobToEmbedDataUrl(blob) {
  try {
    // Small-ish images → embed verbatim (keeps PNG transparency, GIF
    // animation and WebP crispness intact).
    if (blob.size < 2_500_000) return await blobToDataUrl(blob);
    // Big photos → resize at high quality so the HTML stays portable.
    const resized = await resizeBlobForEmbed(blob);
    if (resized) return resized;
    return await blobToDataUrl(blob);
  } catch (err) {
    console.warn("[export] embed failed:", err?.message || err);
    return null;
  }
}

function extensionFromMime(mime, fallback) {
  if (!mime) return fallback;
  if (mime === "image/jpeg") return "jpg";
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  if (mime === "image/gif") return "gif";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("audio/webm")) return "webm";
  if (mime.startsWith("audio/mp4") || mime === "audio/x-m4a") return "m4a";
  if (mime.startsWith("audio/")) return mime.split("/")[1] || "audio";
  if (mime === "text/plain") return "txt";
  return fallback;
}

export async function exportAllNotes({ onProgress } = {}) {
  const live = filterNotes("notes", null, "");
  const archived = filterNotes("archive", null, "");
  const all = [...live, ...archived];
  if (all.length === 0) return 0;

  const zip = new JSZip();
  const stamp = format(new Date(), "yyyy-MM-dd");
  const header = [
    `# Mango Notes export — ${stamp}`,
    "",
    `Exported ${all.length} note${all.length === 1 ? "" : "s"} (${live.length} active, ${archived.length} archived).`,
    "",
    `Open \`notes.html\` for the polished view, or \`notes.md\` for the plain-text copy. Media files live in \`images/\`, \`voice/\` and \`files/\`.`,
    "",
    "---",
    "",
  ].join("\n");
  const mdSections = [header];
  const htmlCards = [];

  let doneCount = 0;
  for (const n of all) {
    const slug = slugify(n.title || n.id);
    const mediaRefs = { images: [], voice: [], docs: [] };
    // Images
    for (let i = 0; i < (n.images || []).length; i++) {
      const path = n.images[i];
      try {
        const blob = await fetchBinary(path);
        const ext = extensionFromMime(blob.type, "jpg");
        const name = `${slug}-${i + 1}.${ext}`;
        zip.file(`images/${name}`, blob);
        // Embed the image inline (original quality under 2.5 MB; resized
        // at high quality beyond that). This replaces the blurry 800 px
        // canvas thumb that made printed PDFs look pixelated.
        const thumb = await blobToEmbedDataUrl(blob);
        mediaRefs.images.push({ path: `images/${name}`, thumb });
      } catch (err) {
        console.warn("[export] skip image", path, err?.message || err);
      }
    }
    // Attachments: separate audio (voice/) from other docs (files/).
    for (let i = 0; i < (n.attachments || []).length; i++) {
      const a = n.attachments[i];
      try {
        const blob = await fetchBinary(a.path);
        const isAudio = (a.content_type || "").startsWith("audio/");
        const ext = extensionFromMime(a.content_type || blob.type, (a.filename || "").split(".").pop() || "bin");
        const name = isAudio ? `${slug}-voice-${i + 1}.${ext}` : `${slug}-${i + 1}.${ext}`;
        const folder = isAudio ? "voice" : "files";
        zip.file(`${folder}/${name}`, blob);
        if (isAudio) {
          mediaRefs.voice.push({ name, path: `${folder}/${name}`, transcript: a.transcript || "" });
        } else {
          mediaRefs.docs.push({ name: a.filename || name, path: `${folder}/${name}` });
        }
      } catch (err) {
        console.warn("[export] skip attachment", a.path, err?.message || err);
      }
    }
    mdSections.push(noteToMarkdown(n, mediaRefs));
    htmlCards.push(noteToHtmlCard(n, mediaRefs, doneCount));
    doneCount++;
    onProgress?.(doneCount, all.length);
  }
  zip.file("notes.md", mdSections.join("\n"));
  zip.file("notes.html", buildHtmlDocument(htmlCards, {
    stamp, total: all.length, live: live.length, archived: archived.length,
  }));

  const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
    onProgress?.(all.length, all.length, meta.percent);
  });
  const defaultName = `mango-notes-${stamp}.zip`;

  // Prefer the File System Access API so the user can pick WHERE to save.
  // Safari / Firefox fall through to a classic download.
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: defaultName,
        types: [{ description: "Mango Notes archive (ZIP)", accept: { "application/zip": [".zip"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return all.length;
    } catch (err) {
      // User cancelled the picker — signal "no-op, don't toast anything".
      if (err?.name === "AbortError") return -1;
      console.warn("[export] showSaveFilePicker failed, falling back:", err?.message || err);
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = defaultName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return all.length;
}
